import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DSBox from "views/system-design/DSBox";
import DSChip from "views/system-design/DSChip";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSSegmentedTabs from "views/system-design/DSSegmentedTabs";
import DSPrimaryButton from "views/system-design/DSPrimaryButton";
import AddIcon from "@mui/icons-material/Add";
import useUsers from "components/hooks/useUsers";
import firebaseService from "firebase_service/firebaseService";
import { ReviewSearchInput } from "api/reviews";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useReviewSearch } from "../hooks/useReviewQueries";
import {
  REVIEW_QUICK_VIEWS,
  REVIEW_REPOS,
  ReviewQuickView,
  ReviewRepo,
  ReviewRequest,
} from "../types";
import ReviewsTable from "./ReviewsTable";
import ReviewDetailPanel from "./ReviewDetailPanel";
import ReviewRequestModal from "./ReviewRequestModal";

const QUICK_VIEW_VALUES = REVIEW_QUICK_VIEWS.map((view) => view.value);
const OPEN_STATUSES: ReviewRequest["status"][] = [
  "open",
  "changes_requested",
  "approved",
];

const EMPTY_TEXT: Record<ReviewQuickView, string> = {
  "to-review": "Nothing waiting for your review.",
  mine: "You have no open review requests.",
  open: "No open review requests.",
  closed: "No closed review requests yet.",
};

export default function ReviewsView() {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: allUsers } = useUsers();
  const users = allUsers ?? [];
  const uid = firebaseService.getUserId() ?? "";

  const rawView = searchParams.get("view");
  const quickView: ReviewQuickView = QUICK_VIEW_VALUES.includes(
    rawView as ReviewQuickView,
  )
    ? (rawView as ReviewQuickView)
    : "to-review";
  const setQuickView = (view: ReviewQuickView) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (view === "to-review") params.delete("view");
        else params.set("view", view);
        return params;
      },
      { replace: true },
    );

  const [repos, setRepos] = useState<ReviewRepo[]>([]);
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  // The selected request lives only in the URL (`?review=<id>`), so Slack
  // deep links, refresh and back button all behave the same way.
  const selectedId = searchParams.get("review");
  const setSelectedId = (id: string | null) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (id) params.set("review", id);
        else params.delete("review");
        return params;
      },
      { replace: true },
    );

  const searchInput = useMemo<ReviewSearchInput>(() => {
    const base: ReviewSearchInput = {
      repos: repos.length ? repos : undefined,
      priority: urgentOnly ? "urgent" : undefined,
      sort: quickView === "closed" ? "-updatedAt" : "-createdAt",
    };
    switch (quickView) {
      case "to-review":
        return { ...base, statuses: ["open"], reviewerId: uid };
      case "mine":
        return { ...base, statuses: OPEN_STATUSES, requesterId: uid };
      case "open":
        return { ...base, statuses: OPEN_STATUSES };
      case "closed":
      default:
        return { ...base, statuses: ["closed"] };
    }
  }, [quickView, repos, urgentOnly, uid]);

  const { requests, total, isLoading } = useReviewSearch(searchInput, {
    enabled: !!uid,
  });

  // The row from the current list, if the selection is in it; the drawer
  // fetches by id anyway, so a deep link outside the list still opens.
  const selectedRow =
    requests.find((request) => request.id === selectedId) ?? null;

  const toggleRepo = (repo: ReviewRepo) =>
    setRepos((prev) =>
      prev.includes(repo) ? prev.filter((r) => r !== repo) : [...prev, repo],
    );

  return (
    <DSBox sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <DSBox
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <DSSegmentedTabs<ReviewQuickView>
          options={REVIEW_QUICK_VIEWS}
          value={quickView}
          onChange={(value) => value && setQuickView(value)}
          size="small"
        />
        <DSBox sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {REVIEW_REPOS.map((repo) => (
            <DSChip
              key={repo.value}
              label={repo.label}
              size="small"
              clickable
              variant={repos.includes(repo.value) ? "filled" : "outlined"}
              color={repos.includes(repo.value) ? "primary" : "default"}
              onClick={() => toggleRepo(repo.value)}
            />
          ))}
          <DSChip
            label="Urgent"
            size="small"
            clickable
            variant={urgentOnly ? "filled" : "outlined"}
            color={urgentOnly ? "error" : "default"}
            onClick={() => setUrgentOnly((prev) => !prev)}
          />
        </DSBox>
        <DSBox sx={{ flex: 1 }} />
        <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
          {total} request{total === 1 ? "" : "s"}
        </DSTypography>
        <DSPrimaryButton
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => setShowCreate(true)}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
        >
          {isMobile ? "New" : "New review request"}
        </DSPrimaryButton>
      </DSBox>

      {isLoading ? (
        <DSBox sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <DSCircularProgress />
        </DSBox>
      ) : (
        <ReviewsTable
          requests={requests}
          users={users}
          selectedId={selectedId ?? undefined}
          onSelect={(request) =>
            setSelectedId(selectedId === request.id ? null : request.id)
          }
          emptyText={EMPTY_TEXT[quickView]}
        />
      )}

      <ReviewDetailPanel
        requestId={selectedId}
        fallback={selectedRow}
        users={users}
        onClose={() => setSelectedId(null)}
      />

      {showCreate && (
        <ReviewRequestModal
          onClose={() => setShowCreate(false)}
          onSaved={(request) => setSelectedId(request.id)}
        />
      )}
    </DSBox>
  );
}
